/**
 * Chat-related utility functions
 */

type ChartSeries = Array<{ name?: string; data: (number | null)[] }> | number[];

export interface ChatChartData {
  type?: 'bar' | 'line' | 'area' | 'donut' | 'pie' | 'radar';
  series: ChartSeries;
  title?: string;
  xaxis?: { categories?: string[] };
  labels?: string[];
  colors?: string[];
}

export type ContentPart = { type: 'text'; content: string } | { type: 'chart'; data: ChatChartData };

/**
 * Parses chat message content and extracts chart blocks.
 *
 * Chart blocks use markdown-style syntax: ```chart\n{json}\n```
 * The JSON content is defined by the backend system prompt.
 *
 * @param content - Raw message content from assistant
 * @returns Array of text and chart parts for rendering
 *
 * @example
 * parseChartBlocks("Here's your data:\n```chart\n{\"type\":\"bar\",\"series\":[1,2,3]}\n```")
 * // Returns: [
 * //   { type: 'text', content: "Here's your data:\n" },
 * //   { type: 'chart', data: { type: 'bar', series: [1,2,3] } }
 * // ]
 */
export function parseChartBlocks(content: string): ContentPart[] {
  const parts: ContentPart[] = [];
  const chartRegex = /```chart\s*([\s\S]*?)```/g;

  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = chartRegex.exec(content)) !== null) {
    // Add text content before chart block
    if (match.index > lastIndex) {
      parts.push({
        type: 'text',
        content: content.slice(lastIndex, match.index),
      });
    }

    // Parse chart JSON
    try {
      const data = JSON.parse(match[1].trim()) as ChatChartData;
      parts.push({ type: 'chart', data });
    } catch (error) {
      console.warn('[Chat] Failed to parse chart JSON:', error);
      // If JSON parsing fails, treat as text
      parts.push({ type: 'text', content: match[0] });
    }

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text content
  if (lastIndex < content.length) {
    parts.push({
      type: 'text',
      content: content.slice(lastIndex),
    });
  }

  return parts.length > 0 ? parts : [{ type: 'text', content }];
}

/**
 * Formats a timestamp for display in chat messages
 * @param date - Message timestamp
 * @returns Formatted time string (HH:MM format)
 */
export function formatMessageTime(date: Date): string {
  return new Date(date).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Truncates long content for preview display
 * @param content - Full message content
 * @param maxLength - Maximum length before truncation
 * @returns Truncated content with ellipsis if needed
 */
export function truncateContent(content: string, maxLength: number = 100): string {
  if (content.length <= maxLength) {
    return content;
  }
  return content.slice(0, maxLength).trim() + '...';
}
